import { useState } from 'react';

import { GoogleSignInCancelledError } from '@/features/identity/hooks/useGoogleOAuth';
import { authService } from '@/services/authService';
import type { OAuthProvider } from '@/types/auth.types';
import { parseRestError } from '@/utils/errorUtils';

/**
 * Google sign-in can fail before any request leaves the browser — a blocked popup, an unconfigured client
 * id — and those failures carry a message that tells the user what to do about it. `parseRestError` reports
 * "Something went wrong" for anything that is not an HTTP failure, which is true but useless when the real
 * problem is "allow popups". Mirrors `ResetMfaDialog`'s handling of the same two failure sources.
 */
function clientOrServerMessage(error: unknown): string {
  const parsed = parseRestError(error);
  return parsed.code === 'Unknown' && error instanceof Error && error.message
    ? error.message
    : parsed.message;
}

/**
 * Drives the provider half of the pre-authentication lost-authenticator flow, in the two steps that keep
 * a provider sign-in from being a reset on its own:
 *
 *  1. `verify` — exchange the provider token for "is there an account I may recover, and which one".
 *     Mutates nothing server-side, so it is safe to run the instant the popup closes.
 *  2. `reset` — called only from the confirmation screen. This is the request that removes the
 *     authenticator, revokes every session and triggers the notification email.
 *
 * The access token is held here between the two so the reset re-presents it: the server re-verifies rather
 * than trusting that step 1 happened, which means no recovery state has to be persisted anywhere.
 */
export function useOAuthMfaRecovery(options: {
  provider: OAuthProvider;
  onVerified: (accountEmail: string) => void;
  onReset: () => void;
}) {
  const { provider, onVerified, onReset } = options;
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Runs step 1 with a token the provider button has just produced. */
  const verify = async (token: string): Promise<void> => {
    setIsBusy(true);
    setError(null);
    try {
      const result = await authService.checkOAuthMfaRecovery({ provider, accessToken: token });
      if (!result.eligible || !result.accountEmail) {
        // Every account-shaped refusal collapses into one message server-side, so there is nothing more
        // specific to say here — and the password field is still on the previous screen to fall back to.
        setError(
          `We couldn't find a Willnq account with an authenticator to reset for that ${provider} sign-in.`,
        );
        return;
      }
      setAccessToken(token);
      onVerified(result.accountEmail);
    } catch (err) {
      setError(clientOrServerMessage(err));
    } finally {
      setIsBusy(false);
    }
  };

  /** Wraps `verify` for a provider button: a dismissed popup is a choice, not an error to report. */
  const verifyWithToken = async (token: string): Promise<void> => {
    try {
      await verify(token);
    } catch (err) {
      if (!(err instanceof GoogleSignInCancelledError)) {
        setError(clientOrServerMessage(err));
      }
    }
  };

  /** Runs step 2. Only ever called from the confirmation screen's own button. */
  const reset = async (): Promise<void> => {
    if (!accessToken) return;
    setIsBusy(true);
    setError(null);
    try {
      await authService.resetMfaViaOAuth({ provider, accessToken });
      onReset();
    } catch (err) {
      setError(clientOrServerMessage(err));
    } finally {
      setIsBusy(false);
    }
  };

  return { isBusy, error, verifyWithToken, reset };
}
